import { API_URLS } from '@chessx/shared/api-routes'
import React, { useEffect, useState } from 'react'
import { Modal, Pagination } from 'react-bootstrap'
import { USER_PAGE_LIMIT } from '../../constants'
import useAdminStore from '../../store/AdminStore'
import './AdminUsers.scss'
import { DateTime } from 'luxon'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../network/axiosInstance'
import { toastErrorMixin, toastMixin } from '../../utils/interact'
import RegistrationComponent from '../../components/Login/RegistrationComponent'

interface AdminUserRow {
  _id: string
  userName?: string
  email?: string
  role?: string
  isActive?: boolean
  profilePic?: string
  lastLogin?: string
  [key: string]: unknown
}

interface UserListResponse {
  data?: AdminUserRow[]
  totalCount?: number
}

const AdminUsers = () => {
  const [page, setPage] = useState(0)
  const [pagesArray, setPagesArray] = useState<number[]>([])
  const { fetchUsers } = useAdminStore()
  const [isLoading, setIsLoading] = useState(false)
  const [userData, setUserData] = useState<UserListResponse | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [registerVisible, setRegisterVisible] = useState(false)
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})
  const navigate = useNavigate()

  const getUser = async () => {
    try {
      setIsLoading(true)
      const response = await fetchUsers({ page, limit: USER_PAGE_LIMIT })
      setUserData(response as UserListResponse)
    } catch (err) {
      console.error('err', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getUser()
  }, [page])

  useEffect(() => {
    if (Array.isArray(userData?.data)) {
      const total = userData?.totalCount ?? 0
      setPagesArray([...Array(Math.ceil(total / USER_PAGE_LIMIT) || 1).keys()])
    }
  }, [userData?.data?.length])

  const setLoading = (id: string, key: string, val: boolean) =>
    setActionLoading(prev => ({ ...prev, [`${id}_${key}`]: val }))

  const onEditClick = (user: AdminUserRow) => navigate('/admin/users/detail', { state: user })

  const onResetClick = async (user: AdminUserRow) => {
    setLoading(user._id, 'reset', true)
    try {
      await axiosInstance.post(API_URLS.ADMIN_RESET_USER, { userId: user._id })
      toastMixin.fire({ title: 'User reset successfully.' })
    } catch {
      toastErrorMixin.fire({ title: 'Error resetting user.' })
    } finally {
      setLoading(user._id, 'reset', false)
    }
  }

  const onDeactivateClick = async (user: AdminUserRow) => {
    setLoading(user._id, 'toggle', true)
    try {
      await axiosInstance.put(API_URLS.ADMIN_UPDATE_USER, {
        isActive: !user.isActive,
        userId: user._id
      })
      getUser()
    } catch {
      toastErrorMixin.fire({ title: 'Error updating user.' })
    } finally {
      setLoading(user._id, 'toggle', false)
    }
  }

  const onDestroyClick = async (user: AdminUserRow) => {
    if (
      !window.confirm(`Permanently delete ${user.userName || user.email}? This cannot be undone.`)
    )
      return
    setLoading(user._id, 'destroy', true)
    try {
      await axiosInstance.delete(API_URLS.ADMIN_DESTROY_USER, {
        params: { userId: user._id }
      })
      getUser()
    } catch {
      toastErrorMixin.fire({ title: 'Error destroying user.' })
    } finally {
      setLoading(user._id, 'destroy', false)
    }
  }

  const syncArmies = async () => {
    setSyncing(true)
    try {
      const res = await axiosInstance.post(API_URLS.ADMIN_SYNC_ARMY)
      if (res.status === 200) toastMixin.fire({ title: 'Sync Successful' })
    } catch {
      toastErrorMixin.fire({ title: 'Error syncing user armies.' })
    } finally {
      setSyncing(false)
    }
  }

  const getInitials = (user: AdminUserRow) =>
    (user.userName || user.email || '?').charAt(0).toUpperCase()

  const formatDate = (date?: string) => {
    if (!date) return '—'
    return DateTime.fromJSDate(new Date(date)).toFormat('MMM dd, yyyy')
  }

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h3 className="users-title">Users</h3>
          {userData && <span className="users-count">{userData.totalCount} total users</span>}
        </div>
        <div className="users-actions">
          <button className="btn-secondary-action" disabled={syncing} onClick={syncArmies}>
            {syncing ? (
              <>
                <span className="btn-spin" /> Syncing...
              </>
            ) : (
              <>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                Sync Armies
              </>
            )}
          </button>
          <button className="btn-primary-action" onClick={() => setRegisterVisible(true)}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New User
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="users-card">
        {isLoading ? (
          <div className="users-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row">
                <div className="skeleton-avatar" />
                <div className="skeleton-lines">
                  <div className="skeleton-line w60" />
                  <div className="skeleton-line w40" />
                </div>
              </div>
            ))}
          </div>
        ) : userData?.data?.length ? (
          <div className="table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Sign In</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userData.data.map((user, index) => (
                  <tr key={user._id || index} className={!user.isActive ? 'row-inactive' : ''}>
                    <td className="col-num">{page * USER_PAGE_LIMIT + index + 1}</td>
                    <td className="col-user" onClick={() => onEditClick(user)}>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {user.profilePic ? (
                            <img src={user.profilePic} alt={user.userName} className="avatar-img" />
                          ) : (
                            getInitials(user)
                          )}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{user.userName || '—'}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-role">
                      <span className={`role-badge role-${user.role}`}>{user.role}</span>
                    </td>
                    <td className="col-status">
                      <span
                        className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}
                      >
                        <span className="status-dot" />
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="col-date">{formatDate(user.lastLogin)}</td>
                    <td className="col-actions">
                      <div className="action-btns">
                        <button
                          className="act-btn act-edit"
                          onClick={() => onEditClick(user)}
                          title="Edit user"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          className="act-btn act-reset"
                          onClick={() => onResetClick(user)}
                          disabled={actionLoading[`${user._id}_reset`]}
                          title="Reset user"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                            <path d="M3 3v5h5" />
                          </svg>
                          Reset
                        </button>
                        <button
                          className={`act-btn ${user.isActive ? 'act-deactivate' : 'act-activate'}`}
                          onClick={() => onDeactivateClick(user)}
                          disabled={actionLoading[`${user._id}_toggle`]}
                          title={user.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="act-btn act-destroy"
                          onClick={() => onDestroyClick(user)}
                          disabled={actionLoading[`${user._id}_destroy`]}
                          title="Delete user"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="users-empty">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#cbd5e1"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p>No users found</p>
          </div>
        )}

        {pagesArray.length > 1 && (
          <div className="users-pagination">
            <Pagination>
              <Pagination.Prev disabled={page === 0} onClick={() => setPage(p => p - 1)} />
              {pagesArray.map(obj => (
                <Pagination.Item key={obj} active={obj === page} onClick={() => setPage(obj)}>
                  {obj + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === pagesArray.length - 1}
                onClick={() => setPage(p => p + 1)}
              />
            </Pagination>
          </div>
        )}
      </div>

      <Modal centered show={registerVisible} onHide={() => setRegisterVisible(false)}>
        <RegistrationComponent
          onSuccess={() => {
            setRegisterVisible(false)
            getUser()
          }}
          isFromUserTable
          toggleRegister={() => setRegisterVisible(false)}
        />
      </Modal>
    </div>
  )
}

export default AdminUsers
